import React from 'react';
import { bool, func, shape } from 'prop-types';
import { faClock, faNote, faUserCheck, faUserLock, faUserPlus, faUserXmark } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Input, Card, Divider, Button, Form, Radio, Space, Popconfirm } from 'antd';
import { findLast, isEmpty, lte } from 'lodash';
import I18nCustomFormatter from 'Src/common/components/i18nCustomFormatter';

function ConnectToMentor({
  mentorData,
  connectionData,
  handleShowNote,
  form,
  showNote,
  sendRequest,
  withdrawRequest,
  closeModal,
  submitBtnLoading,
}) {
  const renderTitle = (title, Icon, description = '') => (
    <div className="connect-to-mentor-header">
      <div className="status-headline arc-d-flex arc-flex-vertical-align">
        <FontAwesomeIcon icon={Icon} />
        <h5 className="arc-H250 ml8 mb0">{title}</h5>
      </div>
      <If condition={description}>
        <p className="arc-p arc-color-gray9 connect-to-mentor-description">{description}</p>
      </If>
    </div>
  );

  const getConnectionStatus = () => {
    if (isEmpty(mentorData.connections)) {
      return false;
    }

    const connectionDetails = findLast(
      mentorData.connections,
      (connection) => connection.mentee === window.userData.id,
    );

    if (!connectionDetails) {
      return false;
    }

    if (connectionDetails.status === 1) {
      return 'requested';
    }

    if (connectionDetails.status === 2) {
      return 'connected';
    }

    return false;
  };

  const connectionToMentorScreen = (isRequestSent = false, id = null) => {
    const label = isRequestSent ? 'You sent request for' : "I'd like to connect for";

    if (id) {
      form.setFieldsValue({ service: id });
    }

    return (
      <div className="connect-to-mentor-screen">
        <Form form={form} layout="vertical">
          {renderTitle(
            isRequestSent
              ? `Connection request sent to ${mentorData?.full_name?.split(' ')[0]}`
              : `Connect with ${mentorData?.full_name?.split(' ')[0]}`,
            faUserPlus,
            `You can have up to ${connectionData?.connections_max} connections`,
          )}
          <Divider />
          <div className="connect-to-mentor-form">
            <Form.Item
              name="service"
              rules={[{ required: true, message: 'Please select a service' }]}
              label={<h6 className="arc-H200">{label}</h6>}>
              <Radio.Group disabled={isRequestSent}>
                <Space direction="vertical">
                  {mentorData?.mentor_services?.map((service) => (
                    <Radio key={service.id} value={service.id}>
                      {service.name}
                    </Radio>
                  ))}
                </Space>
              </Radio.Group>
            </Form.Item>
            <If condition={!isRequestSent && !showNote}>
              <Button
                type="link"
                className="send-with-note-btn"
                onClick={handleShowNote}
                icon={<FontAwesomeIcon icon={faNote} className="mr8" />}>
                <I18nCustomFormatter id="send-request-with-note" />
              </Button>
            </If>
            <If condition={!isRequestSent && showNote}>
              <Form.Item name="note">
                <TextArea rows={4} placeholder="Add a note" />
              </Form.Item>
            </If>
          </div>
          <Divider />
          <div className="connection-footer-buttons arc-d-flex arc-flex-vertical-align">
            <Choose>
              <When condition={isRequestSent}>
                <div>
                  <FontAwesomeIcon icon={faClock} className="mr8" />
                  <I18nCustomFormatter id="request-sent-label" />
                </div>
                <Popconfirm
                  onConfirm={withdrawRequest}
                  okText="Yes"
                  cancelText="No"
                  overlayClassName="withdraw-request-popover"
                  title={
                    <div>
                      <h6 className="arc-H150">
                        <I18nCustomFormatter id="withdraw-request-label" />
                      </h6>
                      <p className="arc-p">
                        <I18nCustomFormatter id="withdraw-request-question" />
                      </p>
                    </div>
                  }
                  icon={<FontAwesomeIcon icon={faUserXmark} />}>
                  <Button className="ml12 withdraw-request-btn" loading={submitBtnLoading}>
                    <I18nCustomFormatter id="withdraw-request-label" />
                  </Button>
                </Popconfirm>
              </When>
              <Otherwise>
                <Button onClick={closeModal} className="cancel-btn">
                  <I18nCustomFormatter id="cancel-btn" />
                </Button>
                <Button type="primary" className="ml12" onClick={sendRequest} loading={submitBtnLoading}>
                  <I18nCustomFormatter id="send-request" />
                </Button>
              </Otherwise>
            </Choose>
          </div>
        </Form>
      </div>
    );
  };

  const getContent = () => {
    const status = getConnectionStatus();
    const noConnectionsLeft = lte(connectionData?.connections_available, 0);
    const isAcceptingConnections = lte(mentorData?.connections_available, 0);

    if (status === 'connected') {
      return renderTitle(`You are already connected with ${mentorData?.full_name?.split(' ')[0]}`, faUserCheck);
    }

    if (status === 'requested') {
      const connectionDetails = findLast(
        mentorData.connections,
        (connection) => connection.mentee === window.userData.id,
      );

      return connectionToMentorScreen(true, connectionDetails.service.id);
    }

    if (noConnectionsLeft) {
      return renderTitle(
        'You have reached your connection limit',
        faUserCheck,
        <p>
          You are currently utilising all {connectionData?.connections_max} connections. View your{' '}
          <a href="/mentorship/connections">connections dashboard</a> to manage your connections.
        </p>,
      );
    }

    if (isAcceptingConnections) {
      return renderTitle(`${mentorData?.full_name?.split(' ')[0]} is unavailable at the moment`, faUserLock);
    }

    return connectionToMentorScreen();
  };

  return (
    <div className="connect-to-mentor-wrapper mt6">
      <Card>{getContent()}</Card>
    </div>
  );
}

ConnectToMentor.propTypes = {
  closeModal: func.isRequired,
  connectionData: shape().isRequired,
  form: shape().isRequired,
  handleShowNote: func.isRequired,
  mentorData: shape().isRequired,
  sendRequest: func.isRequired,
  showNote: bool.isRequired,
  submitBtnLoading: bool.isRequired,
  withdrawRequest: func.isRequired,
};

export default ConnectToMentor;
const { TextArea } = Input;
